import React from "react";
import { FaCalendarPlus, FaChevronDown } from "react-icons/fa";

import SchoolYearBadge from "./SchoolYearBadge";

type IconComponent = React.ComponentType<{ className?: string }>;

const CalendarPlusIcon = FaCalendarPlus as IconComponent;
const ChevronDownIcon = FaChevronDown as IconComponent;

const SchoolYearSelector = ({
  t,
  selectedSchoolYear,
  selectedSchoolYearUuid,
  schoolYearLabel,
  isCurrentSchoolYear,
  schoolYearDropdownRef,
  schoolYearDropdownOpen,
  setSchoolYearDropdownOpen,
  schoolYearGroups,
  renderSchoolYearOption,
  activePlanningTab,
  canAdd,
  openOfficialHolidayModal,
  officialHolidaysLoading,
}: any) => (
  <section className="lumen-plain-card rounded-2xl px-4 py-3">
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
        <span className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.schoolYear", { defaultValue: "Schooljaar" })}:
        </span>

        <div className="relative w-full sm:w-80" ref={schoolYearDropdownRef}>
          <button
            type="button"
            onClick={() => setSchoolYearDropdownOpen((open: boolean) => !open)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
            aria-expanded={schoolYearDropdownOpen}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">
                {selectedSchoolYear
                  ? schoolYearLabel(selectedSchoolYear)
                  : t("schoolCalendar.schoolYearPlaceholder", {
                      defaultValue: "Kies een schooljaar",
                    })}
              </span>

              {selectedSchoolYear && isCurrentSchoolYear(selectedSchoolYear) && (
                <SchoolYearBadge>
                  {t("schoolCalendar.schoolYear.current", {
                    defaultValue: "Huidig actief",
                  })}
                </SchoolYearBadge>
              )}
            </span>

            <ChevronDownIcon className="shrink-0 text-gray-500" />
          </button>

          {schoolYearDropdownOpen && (
            <div className="absolute left-0 right-0 z-30 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white py-2 shadow-xl dark:border-gray-700 dark:bg-gray-900">
              {schoolYearGroups.recommended.length > 0 && (
                <div>
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("schoolCalendar.schoolYear.recommended", {
                      defaultValue: "Aanbevolen",
                    })}
                  </div>
                  {schoolYearGroups.recommended.map((year: any) => renderSchoolYearOption(year))}
                </div>
              )}

              {schoolYearGroups.upcoming.length > 0 && (
                <div className="mt-1 border-t border-gray-100 pt-1 dark:border-gray-800">
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("schoolCalendar.schoolYear.upcoming", {
                      defaultValue: "Komende schooljaren",
                    })}
                  </div>
                  {schoolYearGroups.upcoming.map((year: any) => renderSchoolYearOption(year))}
                </div>
              )}

              {schoolYearGroups.archived.length > 0 && (
                <div className="mt-1 border-t border-gray-100 pt-1 dark:border-gray-800">
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("schoolCalendar.schoolYear.archived", {
                      defaultValue: "Oude schooljaren",
                    })}
                  </div>
                  {schoolYearGroups.archived.map((year: any) =>
                    renderSchoolYearOption(year, { archive: true }),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
        {activePlanningTab === "yearPlanner" && canAdd && (
          <button
            type="button"
            onClick={openOfficialHolidayModal}
            disabled={!selectedSchoolYearUuid || officialHolidaysLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <CalendarPlusIcon />
            {t("schoolCalendar.officialHolidays.button", {
              defaultValue: "Vakantiedagen toevoegen",
            })}
          </button>
        )}

        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t("schoolCalendar.schoolYear.contextHint", {
            defaultValue:
              "Nieuwe planning-items worden automatisch aan dit schooljaar gekoppeld.",
          })}
        </p>
      </div>
    </div>
  </section>
);

export default SchoolYearSelector;
